import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { ObjectDetail, ObjectNode, Plan, Staff, Template } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { AssigneePicker, type Assignment } from '../components/people.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Modal, Notice, Spinner } from '../components/ui.js'
import { RecurrenceEditor, type Recurrence } from './RecurrenceEditor.js'
import { addDays, formatDate, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { SearchResults, Tree } from './Objects.js'
import { PlanFormModal, draftFromPlan, emptyPlanDraft, keptNote, spreadLabel, type PlanDraft } from './PlanForm.js'
import { ListImportModal } from '../components/ListImport.js'

/**
 * All plans, with the filters other pages link into: `?mitarbeiter=`,
 * `?leistung=`, `?objekt=` narrow the list, `?plan=` opens one for editing.
 */
export function PlansPage() {
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [draft, setDraft] = useState<PlanDraft | null>(null)
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [treeSearch, setTreeSearch] = useState('')
  const [importing, setImporting] = useState(false)
  const toast = useToast()

  const assigneeId = params.get('mitarbeiter') ?? ''
  const templateId = params.get('leistung') ?? ''
  const objectId = params.get('objekt') ?? ''
  const editId = params.get('plan')

  const plans = useQuery({
    queryKey: ['plans', objectId],
    queryFn: () => api<Plan[]>(objectId ? `/api/plans?objectId=${objectId}` : '/api/plans'),
  })
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const node = useQuery({
    queryKey: ['object', objectId],
    queryFn: () => api<ObjectDetail>(`/api/objects/${objectId}`),
    enabled: Boolean(objectId),
  })

  /** The tree on the left narrows the list to an object and everything below it. */
  function pickObject(picked: ObjectNode | null) {
    const next = new URLSearchParams(params)
    if (picked) next.set('objekt', picked.id)
    else next.delete('objekt')
    setParams(next, { replace: true })
    setSelected(new Set())
  }
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api<Staff[]>('/api/staff') })

  // Deep link from a task or an object: open that plan straight away.
  useEffect(() => {
    if (!editId || !plans.data) return
    const plan = plans.data.find((p) => p.id === editId)
    if (plan) setDraft(draftFromPlan(plan))
  }, [editId, plans.data])

  // Opened from a task: closing goes back to that list, filters and scroll included.
  const navigate = useNavigate()
  function closeDraft() {
    setDraft(null)
    if (editId && params.get('zurueck')) navigate(-1)
    else if (editId) clearParam('plan')
  }

  const reactivate = useMutation({
    mutationFn: (id: string) => api(`/api/plans/${id}`, { method: 'PATCH', body: { active: true } }),
    onSuccess: async () => {
      toast('Plan wieder aktiv, die Aufgaben wurden neu erzeugt.')
      await queryClient.invalidateQueries({ queryKey: ['plans'] })
    },
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => api<{ removedFutureTasks: number }>(`/api/plans/${id}`, { method: 'DELETE' }),
    onSuccess: async (result, id) => {
      toast(`Plan deaktiviert. ${result.removedFutureTasks} noch offene künftige Aufgaben wurden entfernt.`, {
        action: { label: 'Rückgängig', onAction: () => reactivate.mutate(id) },
      })
      await queryClient.invalidateQueries({ queryKey: ['plans'] })
    },
  })

  const canCreate = (templates.data?.length ?? 0) > 0
  const term = filter.trim().toLowerCase()
  const visible = useMemo(
    () =>
      (plans.data ?? []).filter(
        (plan) =>
          (!assigneeId || plan.assigneeId === assigneeId) &&
          (!templateId || plan.templateId === templateId) &&
          (!term || [plan.templateName, plan.siteName, plan.customerName, plan.label, plan.assigneeName, plan.teamName].some((v) => v?.toLowerCase().includes(term))),
      ),
    [plans.data, assigneeId, templateId, term],
  )

  const activeVisible = visible.filter((p) => p.active)
  const selectedIds = [...selected].filter((id) => activeVisible.some((p) => p.id === id))
  const allOn = activeVisible.length > 0 && activeVisible.every((p) => selected.has(p.id))

  const chips = [
    assigneeId ? { key: 'mitarbeiter', label: `Mitarbeiter: ${staff.data?.find((s) => s.id === assigneeId)?.name ?? '…'}` } : null,
    templateId ? { key: 'leistung', label: `Leistung: ${templates.data?.find((t) => t.id === templateId)?.name ?? '…'}` } : null,
    objectId && node.data ? { key: 'objekt', label: `${node.data.name} und darunter` } : null,
  ].filter((c): c is { key: string; label: string } => Boolean(c))

  function clearParam(key: string) {
    const next = new URLSearchParams(params)
    next.delete(key)
    setParams(next, { replace: true })
  }

  return (
    <>
      <PageHead
        title="Aufgabenpläne"
        subtitle="Wer macht was, wo und wie oft. Aus jedem Plan entstehen automatisch die täglichen Aufgaben."
        action={
          <div className="actions">
            <a className="btn secondary" href={`/api/lists/plans.xlsx${objectId ? `?objectId=${objectId}` : ''}`}>
              <Icon name="download" /> Als Excel
            </a>
            <button type="button" className="btn secondary" onClick={() => setImporting(true)}>
              <Icon name="upload" /> Aus Excel importieren
            </button>
            <button type="button" className="btn" disabled={!canCreate} onClick={() => setDraft(emptyPlanDraft(objectId))}>
              <Icon name="plus" /> Neuer Plan
            </button>
          </div>
        }
      />
      <ListImportModal
        open={importing}
        onClose={() => setImporting(false)}
        title="Aufgabenpläne aus Excel"
        endpoint="/api/plans/import"
        nouns={['Plan', 'Pläne']}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['plans'] })
          void queryClient.invalidateQueries({ queryKey: ['occurrences'] })
        }}
        help={
          <>
            <p style={{ marginTop: 0 }}>
              Am einfachsten: „Als Excel“ herunterladen, in Excel ändern (Mitarbeiter, Wochentage, Aktiv) oder Zeilen ergänzen, wieder hochladen.
              Zeilen mit Plan-ID ändern den Plan, Zeilen ohne legen einen neuen an.
            </p>
            <p>
              Spalten: Kunde, Liegenschaft, Gebäude, Einheit, Für jede, <strong>Leistung</strong>, <strong>Rhythmus</strong>, Wochentage, Mitarbeiter,
              Gruppe, Beginn, Ende, Saison von, Saison bis, Uhrzeit, Aktiv. Rhythmus z. B. „wöchentlich“, „alle 2 Wochen“, „monatlich am 15.“,
              „monatlich am 1. Dienstag“, Wochentage z. B. „Mo, Do“.
            </p>
          </>
        }
      />

      {templates.data && !canCreate && (
        <Notice kind="info">
          Für einen Plan wird mindestens eine <Link to="/vorlagen">Leistung</Link> gebraucht. Legen Sie diese zuerst an.
        </Notice>
      )}

      <div className="explorer explorer-narrow">
        <aside className="card tree">
          <div className="tree-search">
            <input type="search" placeholder="Objekt suchen" value={treeSearch} onChange={(e) => setTreeSearch(e.target.value)} aria-label="Objekt suchen" />
          </div>
          <div className="card-body">
            <button type="button" className={`tree-all ${objectId ? '' : 'selected'}`} onClick={() => pickObject(null)}>
              Alle Objekte
            </button>
            {treeSearch.trim() ? (
              <SearchResults term={treeSearch.trim()} selectedId={objectId} onPick={pickObject} />
            ) : (
              <Tree selectedId={objectId} ancestors={node.data?.breadcrumb ?? []} onPick={pickObject} />
            )}
          </div>
        </aside>
        <div style={{ minWidth: 0 }}>
        <section className="card">
          <div className="card-head">
            <input
              type="search"
              placeholder="Filtern: Leistung, Liegenschaft, Kunde, Mitarbeiter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 11px', border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--surface)' }}
            />
            <div style={{ width: 240 }}>
              <AssigneePicker
                value={{ assigneeId: assigneeId || null, assigneeTeamId: null }}
                onChange={(value) => {
                  const next = new URLSearchParams(params)
                  if (value.assigneeId) next.set('mitarbeiter', value.assigneeId)
                  else next.delete('mitarbeiter')
                  setParams(next, { replace: true })
                }}
                dense
                emptyLabel="Alle Personen"
                placeholder="Nach Person filtern"
              />
            </div>
            {chips.filter((chip) => chip.key !== 'mitarbeiter').map((chip) => (
              <button key={chip.key} type="button" className="chip chip-btn" onClick={() => clearParam(chip.key)} title="Filter entfernen">
                {chip.label} <Icon name="close" size={12} />
              </button>
            ))}
            <span className="muted small">{visible.length} Pläne</span>
            {(filter || assigneeId || templateId || objectId) && (
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  setFilter('')
                  setTreeSearch('')
                  const next = new URLSearchParams(params)
                  for (const key of ['mitarbeiter', 'leistung', 'objekt']) next.delete(key)
                  setParams(next, { replace: true })
                }}
              >
                <Icon name="close" size={14} /> Alle Filter zurücksetzen
              </button>
            )}
          </div>
          {selectedIds.length > 0 && (
            <PlanBulkBar
              ids={selectedIds}
              onClear={() => setSelected(new Set())}
              onDone={async () => {
                setSelected(new Set())
                await queryClient.invalidateQueries({ queryKey: ['plans'] })
                await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
              }}
            />
          )}
          {plans.isLoading ? (
            <Spinner />
          ) : visible.length === 0 ? (
            <EmptyState title={term || chips.length ? 'Kein Plan passt zum Filter' : 'Noch keine Aufgabenpläne'} hint="Ein Plan erzeugt die wiederkehrenden Aufgaben automatisch." />
          ) : (
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th className="check-col">
                      <input
                        type="checkbox"
                        aria-label="Alle sichtbaren auswählen"
                        checked={allOn}
                        onChange={() => setSelected(allOn ? new Set() : new Set(activeVisible.map((p) => p.id)))}
                      />
                    </th>
                    <th>Leistung</th>
                    <th>Wo</th>
                    <th>Rhythmus</th>
                    <th>Zuständig</th>
                    <th className="nowrap">Läuft seit</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((plan, index) => [
                  index === 0 || visible[index - 1]!.siteName !== plan.siteName ? (
                    <tr key={`g-${plan.siteId ?? plan.objectId}-${index}`} className="group-row">
                      <td colSpan={7}>
                        {plan.siteName}
                        {plan.customerName && <span className="muted">, {plan.customerName}</span>}
                      </td>
                    </tr>
                  ) : null,
                    <tr key={plan.id} style={plan.active ? undefined : { opacity: 0.5 }} className={selected.has(plan.id) ? 'selected' : undefined}>
                      <td className="check-col">
                        <input
                          type="checkbox"
                          aria-label={`${plan.templateName} auswählen`}
                          disabled={!plan.active}
                          checked={selected.has(plan.id)}
                          onChange={() =>
                            setSelected((current) => {
                              const next = new Set(current)
                              if (next.has(plan.id)) next.delete(plan.id)
                              else next.add(plan.id)
                              return next
                            })
                          }
                        />
                      </td>
                      <td>
                        <Link to={`/vorlagen?bearbeiten=${plan.templateId}`} className="strong" title="Leistung öffnen">
                          {plan.templateName}
                        </Link>
                        {!plan.active && (
                          <span className="badge skipped" style={{ marginLeft: 8 }}>
                            inaktiv
                          </span>
                        )}
                      </td>
                      <td>
                        <Link to={`/objekte/${plan.objectId}`}>
                          {plan.objectKind === 'customer' || plan.objectKind === 'site' ? plan.objectName : plan.siteName}
                        </Link>
                        <div className="muted small">
                          {plan.objectKind !== 'customer' && plan.objectKind !== 'site' ? plan.path.join(' › ') : ''}
                          {plan.customerName && plan.objectKind !== 'customer'
                            ? `${plan.objectKind !== 'site' && plan.path.length ? ', ' : ''}${plan.customerName}`
                            : ''}
                        </div>
                        {spreadLabel(plan) && (
                          <div className="small" style={{ color: 'var(--blue-700)' }}>
                            {spreadLabel(plan)}
                          </div>
                        )}
                      </td>
                      <td>
                        {plan.recurrenceLabel}
                        {plan.dueTime && <div className="muted small">bis {plan.dueTime} Uhr</div>}
                        {plan.seasonLabel && <div className="muted small">Saison {plan.seasonLabel}</div>}
                      </td>
                      <td>
                        {plan.assigneeId ? <Link to={`/mitarbeiter/${plan.assigneeId}`}>{plan.assigneeName}</Link> : plan.teamName ? `Gruppe ${plan.teamName}` : <span className="muted">nicht zugewiesen</span>}
                      </td>
                      <td className="nowrap small">{formatDate(plan.startDate)}</td>
                      <td style={{ textAlign: 'right' }} className="nowrap">
                        <Link className="btn ghost small" to={`/aufgaben?plan=${plan.id}&von=${addDays(todayIso(), -28)}&bis=${addDays(todayIso(), 90)}`} title="Die Aufgaben, die dieser Plan erzeugt hat">
                          <Icon name="list" size={15} /> Aufgaben
                        </Link>{' '}
                        <button type="button" className="btn ghost small" onClick={() => setDraft(draftFromPlan(plan))}>
                          <Icon name="edit" size={15} /> Bearbeiten
                        </button>{' '}
                        {plan.active ? (
                          <button type="button" className="btn ghost small" onClick={() => deactivate.mutate(plan.id)}>
                            Deaktivieren
                          </button>
                        ) : (
                          <button type="button" className="btn ghost small" onClick={() => reactivate.mutate(plan.id)}>
                            Aktivieren
                          </button>
                        )}
                      </td>
                    </tr>
                  ])}
                </tbody>
              </table>
            </div>
          )}
        </section>
        </div>
      </div>

      <PlanFormModal
        draft={draft}
        onClose={closeDraft}
        onSaved={closeDraft}
      />
    </>
  )
}

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' },
  { value: 0, label: 'Sonntag' },
]

/**
 * Viele Pläne auf einmal (Workshop 23.10.): erst die Liste filtern, z. B. nach
 * Objekt oder Person, dann alle ankreuzen und den Wochentag oder die
 * zuständige Person in einem Schritt ändern. Künftige offene Aufgaben werden
 * nach der neuen Regel neu erzeugt.
 */
function PlanBulkBar({ ids, onDone, onClear }: { ids: string[]; onDone: () => void; onClear: () => void }) {
  const toast = useToast()
  const [assignment, setAssignment] = useState<Assignment>({ assigneeId: null, assigneeTeamId: null })
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const [rhythm, setRhythm] = useState<Recurrence | null>(null)
  const run = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ changed: number; skipped: number; keptAdjustedTasks: number }>('/api/plans/bulk', { method: 'POST', body: { ids, ...body } }),
    onSuccess: (result) => {
      toast(
        `${result.changed} ${result.changed === 1 ? 'Plan' : 'Pläne'} geändert` +
          (result.skipped ? `, ${result.skipped} ausgelassen (Tag nicht eindeutig oder kein Wochentag im Rhythmus)` : '') +
          '. Künftige offene Aufgaben wurden neu erzeugt.' +
          keptNote(result.keptAdjustedTasks),
      )
      onDone()
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen.', 'error'),
  })
  return (
    <div className="bulk-bar" role="region" aria-label="Mehrfachauswahl Pläne">
      <span className="strong">{ids.length} ausgewählt</span>
      <div className="bulk-group">
        <AssigneePicker value={assignment} onChange={setAssignment} dense emptyLabel="Niemand" placeholder="Person oder Gruppe" />
        <button
          type="button"
          className="btn small"
          disabled={run.isPending || (!assignment.assigneeId && !assignment.assigneeTeamId)}
          onClick={() => run.mutate({ action: 'assign', ...assignment })}
        >
          Zuweisen
        </button>
      </div>
      <div className="bulk-group">
        <select value={fromDay} onChange={(e) => setFromDay(e.target.value)} aria-label="Bisheriger Tag">
          <option value="">Tag (wenn nur einer)</option>
          {WEEKDAYS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <span className="muted small">wird</span>
        <select value={toDay} onChange={(e) => setToDay(e.target.value)} aria-label="Neuer Tag">
          <option value="">neuer Tag</option>
          {WEEKDAYS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn small"
          disabled={run.isPending || toDay === ''}
          onClick={() => run.mutate({ action: 'weekday', fromWeekday: fromDay === '' ? null : Number(fromDay), toWeekday: Number(toDay) })}
        >
          Tag ändern
        </button>
      </div>
      <button type="button" className="btn small secondary" disabled={run.isPending} onClick={() => setRhythm({ type: 'weekly', weekdays: [2] })}>
        Rhythmus ändern
      </button>
      <button
        type="button"
        className="btn small secondary"
        disabled={run.isPending}
        onClick={() => {
          if (window.confirm(`${ids.length} ${ids.length === 1 ? 'Plan' : 'Pläne'} deaktivieren? Offene künftige Aufgaben fallen weg, Erledigtes bleibt.`)) run.mutate({ action: 'deactivate' })
        }}
      >
        Deaktivieren
      </button>
      <button type="button" className="btn ghost small" onClick={onClear}>
        Auswahl aufheben
      </button>
      <Modal
        open={rhythm !== null}
        title={`Rhythmus für ${ids.length} ${ids.length === 1 ? 'Plan' : 'Pläne'}`}
        onClose={() => setRhythm(null)}
        footer={
          <>
            <button type="button" className="btn secondary" onClick={() => setRhythm(null)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn"
              disabled={run.isPending}
              onClick={() => {
                run.mutate({ action: 'recurrence', recurrence: rhythm })
                setRhythm(null)
              }}
            >
              Übernehmen
            </button>
          </>
        }
      >
        <p className="muted small" style={{ marginTop: 0 }}>Alle gewählten Pläne bekommen diesen Rhythmus. Künftige offene Aufgaben werden danach neu erzeugt.</p>
        {rhythm && <RecurrenceEditor value={rhythm} onChange={setRhythm} />}
      </Modal>
    </div>
  )
}
